/**
 * Toolbar with language-independent Unicode glyph buttons. Clicking a button
 * inserts a snippet after the current selection (or at end of document if
 * nothing is selected). Shift-click inserts BEFORE the selection instead.
 *
 * Info-field buttons (the "Header" group) and the line-break button always
 * land on their own line: the snippet is inserted at the current selection
 * position and the surrounding text is split with newlines as needed, so
 * the positional meaning of fields like K:, M:, L:, V:, … is preserved.
 */

import { AbcDocument } from "../model/document.js";
import { button, el } from "./dom.js";
import { Strings } from "../i18n.js";

export interface ToolbarDeps {
  doc: AbcDocument;
  getSelection: () => { startChar: number; endChar: number } | null;
  setSelection: (s: { startChar: number; endChar: number } | null) => void;
  /** raw-textarea-to-selection binding: when false, clicking / moving the
   *  caret in the raw view no longer drives the element selection, so the
   *  user can edit the text freely without the panel jumping around. */
  getRawSelectEnabled: () => boolean;
  setRawSelectEnabled: (enabled: boolean) => void;
  /** Raw-text pane visibility. The pane can be hidden entirely via the
   *  toolbar; when hidden, the raw-select binding button is irrelevant
   *  and so is also hidden. */
  isRawVisible: () => boolean;
  toggleRawVisible: () => void;
  /** Show/hide the toolbar raw-pane switch (📝). */
  showRawToggleButton?: boolean;
  /** Per-header-field visibility gate for T/C/R/K/L/Q/V actions. */
  isHeaderFieldEnabled?: (name: string) => boolean;
  onRawVisibilityChange: (cb: () => void) => void;
  /** MIDI playback. When `playSupported` is false the buttons are disabled. */
  playSupported: boolean;
  isPlaying: () => boolean;
  play: () => void;
  stop: () => void;
  /** Subscribe to playback-state changes so the toolbar can toggle icons. */
  onPlaybackStateChange: (cb: () => void) => void;
  /** Subscribe to selection changes so the toolbar can toggle the delete button. */
  onSelectionChange: (cb: () => void) => void;
  /** Localized strings. */
  strings: Strings;
}

interface InsertSpec {
  /** visible glyph */
  glyph: string;
  /** accessible title / tooltip */
  title: string;
  /** raw snippet inserted around the selection */
  snippet: string;
  /** true → place snippet on its own line (info fields). The snippet is
   *  inserted at the current selection position, and the surrounding text
   *  is split with newlines as needed so the snippet ends up on a line of
   *  its own. The selection's character offsets retain their meaning, so
   *  positional info fields like K:, M:, L:, [V:n] etc. land where the
   *  user expects. */
  infoField?: boolean;
  /** true → snippet is a bare newline whose only job is to split the line
   *  at the current selection position. */
  lineBreak?: boolean;
  /** true → insert before the selection by default; Shift inserts after
   *  (reverses the normal Shift-to-insert-before convention). */
  defaultBefore?: boolean;
  /** keyboard keys that trigger this insertion. */
  hotkeys?: string[];
  /** false -> keep spec in code, but hide it from toolbar and shortcuts. */
  showInToolbar?: boolean;
  /** Header-field name for policy checks (T/C/R/K/L/Q/V/X). */
  fieldName?: string;
}

export class Toolbar {
  private host: HTMLElement;
  private deps: ToolbarDeps;
  private undoBtn: HTMLButtonElement | null = null;
  private redoBtn: HTMLButtonElement | null = null;
  private deleteBtn: HTMLButtonElement | null = null;
  private playBtn: HTMLButtonElement | null = null;
  private stopBtn: HTMLButtonElement | null = null;
  private rawSelectBtn: HTMLButtonElement | null = null;
  private toggleRawBtn: HTMLButtonElement | null = null;
  private modeGroup: HTMLElement | null = null;
  private insertSpecs: ReadonlyArray<InsertSpec> = [];
  private headerSpecs: ReadonlyArray<InsertSpec> = [];
  private strings: Strings;

  constructor(host: HTMLElement, deps: ToolbarDeps) {
    this.host = host;
    this.deps = deps;
    this.strings = deps.strings;
    this.host.classList.add("abc-gui-toolbar");
    this.render();
    // Refresh undo/redo enabled state whenever the document changes.
    this.deps.doc.on(() => this.updateHistoryButtons());
    this.deps.onPlaybackStateChange(() => this.updatePlaybackButtons());
    this.deps.onSelectionChange(() => this.updateHistoryButtons());
    this.deps.onRawVisibilityChange(() => this.updateRawSelectButton());
    this.updateHistoryButtons();
    this.updatePlaybackButtons();
    this.updateRawSelectButton();
  }

  private render(): void {
    this.host.innerHTML = "";
    const s = this.strings;
    const undoBtn = button("↶", s.toolbar.undo, () => this.deps.doc.undo());
    const redoBtn = button("↷", s.toolbar.redo, () => this.deps.doc.redo());
    this.undoBtn = undoBtn;
    this.redoBtn = redoBtn;
    const deleteBtn = button("✖", s.toolbar.delete, () => this.deleteSelection());
    this.deleteBtn = deleteBtn;
    const historyGroup = el("div", { class: "abc-gui-group", title: s.toolbar.groups.history });
    historyGroup.append(undoBtn, redoBtn, deleteBtn);

    const playBtn = button("▶", s.toolbar.play, () => this.deps.play());
    const stopBtn = button("■", s.toolbar.stop, () => this.deps.stop());
    playBtn.disabled = !this.deps.playSupported;
    stopBtn.disabled = !this.deps.playSupported;
    this.playBtn = playBtn;
    this.stopBtn = stopBtn;
    const playbackGroup = el("div", { class: "abc-gui-group", title: s.toolbar.groups.playback });
    playbackGroup.append(playBtn, stopBtn);

    const rawSelectBtn = button("⇌", s.toolbar.rawSelectOn, () => {
      this.deps.setRawSelectEnabled(!this.deps.getRawSelectEnabled());
      this.updateRawSelectButton();
    });
    this.rawSelectBtn = rawSelectBtn;
    const toggleRawBtn = button("📝", s.toolbar.showRaw, () => {
      this.deps.toggleRawVisible();
      this.updateRawSelectButton();
    });
    this.toggleRawBtn = toggleRawBtn;
    const modeGroup = el("div", { class: "abc-gui-group", title: s.toolbar.groups.modes });
    if (this.deps.showRawToggleButton ?? true) {
      modeGroup.append(toggleRawBtn);
    }
    modeGroup.append(rawSelectBtn);
    this.modeGroup = modeGroup;

    const isHeaderEnabled = (name: string) =>
      this.deps.isHeaderFieldEnabled ? this.deps.isHeaderFieldEnabled(name) : true;

    this.insertSpecs = [
      { glyph: "♪", title: s.toolbar.insert.note, snippet: "C", hotkeys: ["N"] },
      { glyph: "𝄽", title: s.toolbar.insert.rest, snippet: "z", hotkeys: ["Z"] },
      { glyph: "[♪]", title: s.toolbar.insert.chord, snippet: "[CEG]", hotkeys: ["H"] },
      { glyph: "∣", title: s.toolbar.insert.bar, snippet: "|", hotkeys: ["I"] },
      { glyph: "‖", title: s.toolbar.insert.doubleBar, snippet: "||" },
      {
        glyph: "|:",
        title: s.toolbar.insert.startRepeat,
        snippet: "|:",
        defaultBefore: true,
        hotkeys: ["[", "{"]
      },
      { glyph: ":|", title: s.toolbar.insert.endRepeat, snippet: ":|", hotkeys: ["]", "}"] },
      {
        glyph: "↵",
        title: s.toolbar.insert.lineBreak,
        snippet: "\n",
        lineBreak: true,
        hotkeys: ["Enter"]
      }
    ];

    this.headerSpecs = [
      {
        glyph: "X:",
        title: s.toolbar.header.newTune,
        snippet: "X:1\nT:Untitled\nM:4/4\nL:1/8\nK:C",
        infoField: true,
        fieldName: "X",
        hotkeys: ["X"],
        // Multi-tune rendering/editing is currently not exposed in the UI.
        // Keep the implementation, but hide this entry for now.
        showInToolbar: false
      },
      {
        glyph: "T:",
        title: s.toolbar.header.title,
        snippet: "T:Title",
        infoField: true,
        fieldName: "T",
        hotkeys: ["T"],
        showInToolbar: isHeaderEnabled("T")
      },
      {
        glyph: "C:",
        title: s.toolbar.header.composer,
        snippet: "C:Composer",
        infoField: true,
        fieldName: "C",
        hotkeys: ["C"],
        showInToolbar: isHeaderEnabled("C")
      },
      {
        glyph: "R:",
        title: s.toolbar.header.rhythm,
        snippet: "R:Rhythm",
        infoField: true,
        fieldName: "R",
        hotkeys: ["R"],
        showInToolbar: isHeaderEnabled("R")
      },
      {
        glyph: "K:",
        title: s.toolbar.header.key,
        snippet: "K:C",
        infoField: true,
        fieldName: "K",
        hotkeys: ["K"],
        showInToolbar: isHeaderEnabled("K")
      },
      {
        glyph: "M:",
        title: s.toolbar.header.meter,
        snippet: "M:4/4",
        infoField: true,
        fieldName: "M",
        hotkeys: ["M"],
        showInToolbar: isHeaderEnabled("M")
      },
      {
        glyph: "L:",
        title: s.toolbar.header.unitLength,
        snippet: "L:1/8",
        infoField: true,
        fieldName: "L",
        hotkeys: ["L"],
        showInToolbar: isHeaderEnabled("L")
      },
      {
        glyph: "Q:",
        title: s.toolbar.header.tempo,
        snippet: "Q:1/4=120",
        infoField: true,
        fieldName: "Q",
        hotkeys: ["Q"],
        showInToolbar: isHeaderEnabled("Q")
      },
      {
        glyph: "V:",
        title: s.toolbar.header.voice,
        snippet: "V:1",
        infoField: true,
        fieldName: "V",
        hotkeys: ["V"],
        showInToolbar: isHeaderEnabled("V")
      }
    ];

    this.host.append(
      historyGroup,
      playbackGroup,
      modeGroup,
      this.group(s.toolbar.groups.insert, this.insertSpecs),
      this.group(s.toolbar.groups.header, this.headerSpecs)
    );
  }

  /** Swap localized strings. Rebuilds the toolbar DOM. */
  setStrings(strings: Strings): void {
    this.strings = strings;
    this.render();
    this.updateHistoryButtons();
    this.updatePlaybackButtons();
    this.updateRawSelectButton();
  }

  /** Trigger one of the insert/header actions via keyboard hotkey. */
  handleShortcut(key: string, shiftKey: boolean): boolean {
    const norm = key.length === 1 ? key.toUpperCase() : key;
    const allSpecs = [...this.insertSpecs, ...this.headerSpecs].filter(
      (s) => s.showInToolbar !== false
    );
    const spec = allSpecs.find((s) => s.hotkeys?.includes(norm));
    if (!spec) return false;
    this.insert(spec, spec.defaultBefore ? !shiftKey : shiftKey);
    return true;
  }

  private updateHistoryButtons(): void {
    if (this.undoBtn) this.undoBtn.disabled = !this.deps.doc.canUndo();
    if (this.redoBtn) this.redoBtn.disabled = !this.deps.doc.canRedo();
    if (this.deleteBtn) {
      const sel = this.deps.getSelection();
      this.deleteBtn.disabled = !sel || sel.startChar === sel.endChar;
    }
  }

  private deleteSelection(): void {
    const sel = this.deps.getSelection();
    if (!sel) return;
    const start = Math.min(sel.startChar, sel.endChar);
    const end = Math.max(sel.startChar, sel.endChar);
    if (start === end) return;

    const findNeighborAfterDelete = (): { startChar: number; endChar: number } | null => {
      const ranges: Array<{ startChar: number; endChar: number }> = [];
      const seen = new Set<string>();
      this.deps.doc.forEachElement((el) => {
        const key = `${el.startChar}:${el.endChar}`;
        if (seen.has(key)) return;
        seen.add(key);
        ranges.push({ startChar: el.startChar, endChar: el.endChar });
      });
      if (ranges.length === 0) return null;
      ranges.sort((a, b) =>
        a.startChar === b.startChar
          ? a.endChar - b.endChar
          : a.startChar - b.startChar
      );
      return (
        ranges.find((r) => r.startChar >= start) ??
        ranges[ranges.length - 1] ??
        null
      );
    };

    this.deps.doc.replace(start, end, "");
    this.deps.setSelection(findNeighborAfterDelete());
  }

  private updatePlaybackButtons(): void {
    if (!this.playBtn || !this.stopBtn) return;
    const supported = this.deps.playSupported;
    const playing = supported && this.deps.isPlaying();
    this.playBtn.classList.toggle("active", playing);
    this.playBtn.disabled = !supported;
    this.stopBtn.disabled = !supported;
  }

  private updateRawSelectButton(): void {
    const rawVisible = this.deps.isRawVisible();
    if (this.toggleRawBtn) {
      this.toggleRawBtn.classList.toggle("active", rawVisible);
      this.toggleRawBtn.title = rawVisible
        ? this.strings.toolbar.hideRaw
        : this.strings.toolbar.showRaw;
    }
    if (this.rawSelectBtn) {
      // The raw-select binding toggle is meaningless when the raw pane is
      // hidden; hide it rather than leaving a stale control in place.
      this.rawSelectBtn.hidden = !rawVisible;
      if (rawVisible) {
        const on = this.deps.getRawSelectEnabled();
        this.rawSelectBtn.classList.toggle("active", on);
        this.rawSelectBtn.title = on
          ? this.strings.toolbar.rawSelectOn
          : this.strings.toolbar.rawSelectOff;
      }
    }
  }

  private group(name: string, specs: ReadonlyArray<InsertSpec>): HTMLElement {
    const g = el("div", { class: "abc-gui-group", title: name });
    for (const spec of specs) {
      if (spec.showInToolbar === false) continue;
      g.append(
        button(spec.glyph, spec.title, (ev) => this.insert(spec, spec.defaultBefore ? !ev.shiftKey : ev.shiftKey))
      );
    }
    return g;
  }

  private insert(spec: InsertSpec, before: boolean): void {
    const sel = this.deps.getSelection();
    const doc = this.deps.doc;
    const src = doc.value;

    if (spec.lineBreak) {
      const anchor = sel ? (before ? sel.startChar : sel.endChar) : src.length;
      if (!this.isMusicLineAnchor(src, anchor)) {
        return;
      }
      if (before) {
        // Shift: remove the newline closest to the current selection range.
        const rangeStart = sel ? Math.min(sel.startChar, sel.endChar) : src.length;
        const rangeEnd = sel ? Math.max(sel.startChar, sel.endChar) : src.length;

        // If a newline is inside the selected range, remove that one first.
        let nlPos = src.indexOf("\n", rangeStart);
        if (nlPos < 0 || nlPos > rangeEnd) {
          const prev = src.lastIndexOf("\n", Math.max(0, rangeStart - 1));
          const next = src.indexOf("\n", rangeEnd);
          const prevDist = prev >= 0 ? rangeStart - prev : Number.POSITIVE_INFINITY;
          const nextDist = next >= 0 ? next - rangeEnd : Number.POSITIVE_INFINITY;
          nlPos = prevDist <= nextDist ? prev : next;
        }
        if (nlPos < 0) return;
        doc.replace(nlPos, nlPos + 1, "");
        this.deps.setSelection({ startChar: nlPos, endChar: nlPos });
        return;
      }
      // A bare line break: just insert the snippet at the current position.
      // No wrapping — the snippet IS the newline.
      if (this.wouldCreateEmptyLine(src, anchor)) {
        return;
      }
      doc.replace(anchor, anchor, spec.snippet);
      const afterBreak = anchor + spec.snippet.length;
      const next = this.findNextElementAfter(afterBreak);
      if (next) {
        this.deps.setSelection(next);
      } else {
        // Fallback: place a caret after the inserted newline.
        this.deps.setSelection({ startChar: afterBreak, endChar: afterBreak });
      }
      return;
    }

    if (spec.infoField) {
      // Info fields must live on their own line. Pin the insertion point to
      // the current selection position (preserving the positional meaning of
      // K:/M:/L:/V: etc.) and surround the snippet with the newlines needed
      // to keep it on a line of its own — splitting the surrounding line if
      // the anchor is mid-line.
      const anchor = sel ? (before ? sel.startChar : sel.endChar) : src.length;
      const needLeadingNL = anchor > 0 && src[anchor - 1] !== "\n" ? "\n" : "";
      const needTrailingNL =
        anchor < src.length && src[anchor] !== "\n" ? "\n" : "";
      const text = needLeadingNL + spec.snippet + needTrailingNL;
      doc.replace(anchor, anchor, text);
      const selStart = anchor + needLeadingNL.length;
      this.deps.setSelection({
        startChar: selStart,
        endChar: selStart + spec.snippet.length
      });
      return;
    }

    if (sel) {
      const pos = before ? sel.startChar : sel.endChar;
      doc.replace(pos, pos, spec.snippet);
      this.deps.setSelection({
        startChar: pos,
        endChar: pos + spec.snippet.length
      });
    } else {
      const end = src.length;
      doc.replace(end, end, spec.snippet);
      this.deps.setSelection({
        startChar: end,
        endChar: end + spec.snippet.length
      });
    }
  }

  /** Find the next parsed music element whose start is at/after `offset`. */
  private findNextElementAfter(
    offset: number
  ): { startChar: number; endChar: number } | null {
    let best: { startChar: number; endChar: number } | null = null;
    this.deps.doc.forEachElement((el) => {
      if (el.startChar < offset) return;
      if (!best || el.startChar < best.startChar) {
        best = { startChar: el.startChar, endChar: el.endChar };
      }
    });
    return best;
  }

  /** True when `anchor` sits on a line that contains parsed music elements. */
  private isMusicLineAnchor(src: string, anchor: number): boolean {
    const clamped = Math.max(0, Math.min(anchor, src.length));
    const probe = clamped === src.length && clamped > 0 ? clamped - 1 : clamped;

    let lineStart = probe;
    while (lineStart > 0 && src[lineStart - 1] !== "\n") lineStart--;
    let lineEnd = probe;
    while (lineEnd < src.length && src[lineEnd] !== "\n") lineEnd++;

    let hasMusic = false;
    this.deps.doc.forEachElement((el) => {
      if (hasMusic) return;
      if (el.startChar >= lineStart && el.startChar < lineEnd) hasMusic = true;
    });
    return hasMusic;
  }

  /** Splitting at start/end of a line would create an empty line. */
  private wouldCreateEmptyLine(src: string, anchor: number): boolean {
    if (anchor < 0 || anchor > src.length) return true;
    const leftIsBreak = anchor > 0 && src[anchor - 1] === "\n";
    const rightIsBreak = anchor < src.length && src[anchor] === "\n";
    return leftIsBreak || rightIsBreak;
  }
}
